/**
 * Group Form Component
 * 
 * Form for creating and editing groups
 * - Zod validation schema
 * - React Hook Form integration
 * - Accessible form controls
 * - Error messages
 * - Loading states
 * - Character counter for description
 * 
 * @component
 * @example
 * <GroupForm
 *   mode="create"
 *   onSubmit={(data) => createGroup(data)}
 *   onCancel={() => setOpen(false)}
 * />
 */

import { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiAlertCircle } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import { organizationService } from '@/services';
import type { Organization } from '@/services';
import { 
  groupFormSchema, 
  type GroupFormProps, 
  type GroupFormData 
} from './GroupForm.types';
import styles from './GroupForm.module.css';

const GroupForm: FC<GroupFormProps> = ({
  initialValues,
  mode,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  // Character count state for description
  const [descriptionLength, setDescriptionLength] = useState(0);
  
  // Organization select states
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);

  // Initialize React Hook Form with Zod resolver
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm<GroupFormData>({
    resolver: zodResolver(groupFormSchema),
    defaultValues: {
      organizationId: initialValues?.organizationId,
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      status: initialValues?.status || 'active',
    },
  });

  /**
   * Load organizations on mount
   */
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        console.log('🔍 Iniciando carregamento de organizações...');
        
        // Buscar organizações da API
        const response = await organizationService.getAll(
          { page: 0, size: 100, sort: 'name,asc' }
        );
        
        console.log('📋 Response completa:', response);
        console.log('📋 Organizações carregadas:', response.data);
        console.log('📋 Total de organizações:', response.data?.length);
        
        setOrganizations(response.data || []);
      } catch (error) {
        console.error('❌ Erro ao carregar organizações:', error);
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    loadOrganizations();
  }, []);

  // Watch description field for character count
  const description = watch('description');
  useEffect(() => {
    setDescriptionLength(description?.length || 0);
  }, [description]);

  // Reset form when initialValues change
  useEffect(() => {
    if (initialValues && organizations.length > 0) {
      reset({
        organizationId: initialValues.organizationId,
        name: initialValues.name || '',
        description: initialValues.description || '',
        status: initialValues.status || 'active',
      });
    }
  }, [initialValues, organizations, reset]);

  /**
   * Handle form submission
   */
  const onSubmitForm = async (data: GroupFormData) => {
    await onSubmit(data);
  };

  /**
   * Get character counter class based on length
   */
  const getCharCounterClass = () => {
    if (descriptionLength > 450) return styles.danger;
    if (descriptionLength > 400) return styles.warning;
    return '';
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmitForm)} noValidate>
      {/* Organization Select */}
      <div className={styles.formGroup}>
        <label htmlFor="organizationId" className={styles.label}>
          Organização <span className={styles.required}>*</span>
        </label>
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <select
              id="organizationId"
              className={styles.select}
              aria-invalid={!!errors.organizationId}
              {...field}
              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
              value={field.value || ''}
              disabled={isLoading || isLoadingOrgs}
            >
              <option value="">
                {isLoadingOrgs ? 'Carregando organizações...' : 'Selecione uma organização'}
              </option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
        />
        {errors.organizationId && (
          <span id="org-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.organizationId.message}
          </span>
        )}
        <span className={styles.helpText}>
          Selecione a organização à qual o grupo pertence
        </span>
        {!isLoadingOrgs && organizations.length === 0 && (
          <p className={styles.error}>
            Nenhuma organização ativa encontrada. Por favor, crie uma organização primeiro.
          </p>
        )}
      </div>

      {/* Name Field */}
      <div className={styles.formGroup}>
        <label htmlFor="name" className={styles.label}>
          Nome do Grupo <span className={styles.required}>*</span>
        </label>
        <input
          id="name"
          type="text"
          className={styles.input}
          {...register('name')}
          placeholder="Digite o nome do grupo"
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'name-error' : undefined}
          disabled={isLoading}
          autoFocus
        />
        {errors.name && (
          <span id="name-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.name.message}
          </span>
        )}
      </div>

      {/* Description Field */}
      <div className={styles.formGroup}>
        <label htmlFor="description" className={styles.label}>
          Descrição
        </label>
        <textarea
          id="description"
          className={styles.textarea}
          {...register('description')}
          placeholder="Digite uma descrição para o grupo (opcional)"
          aria-invalid={!!errors.description}
          aria-describedby={errors.description ? 'description-error description-help' : 'description-help'}
          disabled={isLoading}
          maxLength={500}
        />
        {errors.description && (
          <span id="description-error" className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.description.message}
          </span>
        )}
        <div className={`${styles.charCounter} ${getCharCounterClass()}`}>
          {descriptionLength} / 500 caracteres
        </div>
      </div>

      {/* Status Field */}
      <div className={styles.formGroup}>
        <label htmlFor="status" className={styles.label}>
          Status <span className={styles.required}>*</span>
        </label>
        <Controller
          name="status"
          control={control}
          render={({ field }) => (
            <select
              id="status"
              className={styles.select}
              {...field}
              disabled={isLoading}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          )}
        />
        {errors.status && (
          <span className={styles.error}>
            <FiAlertCircle size={14} />
            {errors.status.message}
          </span>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
        >
          {mode === 'create' ? 'Criar Grupo' : 'Salvar Alterações'}
        </Button>
      </div>
    </form>
  );
};

export default GroupForm;
